import { Box, Heading, IconButton, Input, InputGroup, InputRightElement, Stack, Tag, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { RequestDraftRoute, RequestDraftStatus } from '@prisma/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HiXMark } from 'react-icons/hi2';
import { useSearchParams } from 'react-router-dom';

import { MultiSwitch } from '../components/MultiSwitch';
import { DraftListItem } from '../components/RequestDrafts/DraftListItem';
import { RequestDraftDetail } from '../components/RequestDrafts/RequestDraftDetail';
import { DraftListItemSkeleton } from '../components/RequestDrafts/Skeletons/DraftListItemSkeleton';
import { DraftListSkeleton } from '../components/RequestDrafts/Skeletons/DraftListSkeleton';
import { statusLabel } from '../components/RequestDrafts/types';
import { trpc } from '../config/trpc';
import { DraftListRow, useDraftList } from '../hooks/requestDraft/useDraftList';

type RouteLabel = { label: string; colorScheme: string };

const UNLABELLED_ROUTE: RouteLabel = { label: 'Other', colorScheme: 'gray' };

/**
 * Only the routes the classifier still produces. `RequestDraftRoute` keeps the retired ops-lifecycle values — Postgres
 * cannot drop an enum value in place, and old rows reference them — but the taxonomy collapsed to
 * `booking_confirmation` + `other`, so labelling the rest was describing a system that no longer exists.
 */
const ROUTE_LABELS: Partial<Record<RequestDraftRoute, RouteLabel>> = {
  [RequestDraftRoute.BookingConfirmation]: { label: 'Booking confirmation', colorScheme: 'green' },
  [RequestDraftRoute.Other]: UNLABELLED_ROUTE,
};

const labelFor = (route: RequestDraftRoute): RouteLabel => ROUTE_LABELS[route] ?? UNLABELLED_ROUTE;

// The inbox shows exactly one route. Booking confirmations are the workflow being built; every other route is recorded
// in the ledger for measurement but has no review view, so surfacing it here would only be noise.
const INBOX_ROUTE = RequestDraftRoute.BookingConfirmation;

/** The row to open once `resolvedId` leaves the list: the one below it, else the one above, else none. */
const neighbourIdOf = (rows: DraftListRow[], resolvedId: number): number | null => {
  const index = rows.findIndex((row) => row.id === resolvedId);
  if (index < 0) return null;
  const below = rows[index + 1];
  if (below) return below.id;
  const above = rows[index - 1];
  if (above) return above.id;
  return null;
};

/** No draft has been discarded or deleted on this tab yet. */
const NO_RESOLVED_DRAFTS: ReadonlySet<number> = new Set();

/** What an empty list means depends on why it is empty: a search that found nothing, or an empty queue. */
const emptyMessageFor = (tabLabel: string, query: string) => {
  if (query) return `No ${tabLabel} drafts match "${query}".`;
  return `No ${tabLabel} booking confirmations.`;
};

export const AiInboxScreen = () => {
  const [status, setStatus] = useState<RequestDraftStatus>(RequestDraftStatus.Pending);
  const [searchParams] = useSearchParams();
  const deepLinkedDraftId = Number(searchParams.get('draft')) || null;
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(deepLinkedDraftId);
  // The list keeps showing a discarded or deleted draft until its refetch lands, so neither the next pick nor the
  // auto-select may land on one in the meantime.
  const [resolvedDraftIds, setResolvedDraftIds] = useState(NO_RESOLVED_DRAFTS);

  const { rows, query, setQuery, isLoading, isLoadingMore, triggerIndex, loadMore, refetch } = useDraftList(
    status,
    INBOX_ROUTE,
  );
  const { data: counts, refetch: refetchCounts } = trpc.getRequestDraftCounts.useQuery({ route: INBOX_ROUTE });

  const statusOptions = useMemo(
    () =>
      Object.values(RequestDraftStatus).map((value) => ({
        value,
        label: counts ? `${statusLabel[value]} (${counts[value]})` : statusLabel[value],
      })),
    [counts],
  );

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const tabLabel = statusLabel[status].toLowerCase();
  const emptyMessage = emptyMessageFor(tabLabel, trimmedQuery);
  const showEmpty = !isLoading && rows.length === 0;
  const selectableRows = useMemo(() => rows.filter((row) => !resolvedDraftIds.has(row.id)), [rows, resolvedDraftIds]);

  // Only ever fills an EMPTY selection. The detail pane loads by id and does not need the row, so a deep-linked draft
  // that is not on the first page stays open. Discard and delete move on via handleResolved.
  useEffect(() => {
    if (selectedDraftId) return;
    const [firstRow] = selectableRows;
    if (firstRow) setSelectedDraftId(firstRow.id);
  }, [selectableRows, selectedDraftId]);

  const handleStatusChange = useCallback((value: RequestDraftStatus) => {
    setStatus(value);
    setSelectedDraftId(null);
    setResolvedDraftIds(NO_RESOLVED_DRAFTS);
  }, []);

  const handleResolved = useCallback(async () => {
    if (!selectedDraftId) return;
    setResolvedDraftIds((ids) => new Set(ids).add(selectedDraftId));
    setSelectedDraftId(neighbourIdOf(selectableRows, selectedDraftId));
    await Promise.all([refetch(), refetchCounts()]);
  }, [selectableRows, selectedDraftId, refetch, refetchCounts]);

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" align="center" w="full" spacing="4">
          <Heading variant="h3">AI Inbox</Heading>
          <Tag colorScheme={labelFor(INBOX_ROUTE).colorScheme}>{labelFor(INBOX_ROUTE).label}</Tag>
          <MultiSwitch
            options={statusOptions}
            value={status}
            onChange={(value) => handleStatusChange(value as RequestDraftStatus)}
          />
        </Stack>
      </Stack>

      <Stack direction={{ base: 'column', lg: 'row' }} flex="1" spacing="0" overflow="hidden" borderTopWidth="1px">
        {/* List */}
        <Stack
          w={{ base: 'full', lg: 'sm' }}
          maxH={{ base: '80', lg: 'unset' }}
          flexShrink="0"
          spacing="0"
          borderRightWidth={{ base: 0, lg: '1px' }}
          borderBottomWidth={{ base: '1px', lg: 0 }}
        >
          <Stack direction="row" align="center" p="2" spacing="2" borderBottomWidth="1px">
            <InputGroup size="sm">
              <Input
                type="text"
                autoComplete="off"
                variant="filled"
                borderRadius="md"
                placeholder="Search subject, sender, R-number or AWB"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Show if={isSearching}>
                <InputRightElement width="2.2rem">
                  <IconButton
                    aria-label="Clear search"
                    onClick={() => setQuery('')}
                    icon={<HiXMark />}
                    size="xs"
                    variant="ghost"
                  />
                </InputRightElement>
              </Show>
            </InputGroup>
          </Stack>

          <Box flex="1" minH="0" overflowY="auto">
            <Show if={isLoading}>
              <DraftListSkeleton />
            </Show>
            <Show if={showEmpty}>
              <Text p="4" fontSize="sm" color="gray.500">
                {emptyMessage}
              </Text>
            </Show>
            {rows.map((row, index) => {
              const isSelected = row.id === selectedDraftId;
              const onInView = index === triggerIndex ? loadMore : undefined;

              return (
                <DraftListItem
                  key={row.id}
                  row={row}
                  isSelected={isSelected}
                  onSelect={() => setSelectedDraftId(row.id)}
                  onInView={onInView}
                />
              );
            })}
            {/* The next page announcing itself, so the list never looks like it has ended when it has not. */}
            <Show if={isLoadingMore}>
              <DraftListItemSkeleton />
            </Show>
          </Box>
        </Stack>

        {/* Review pane */}
        <Box flex="1" minW="0" overflow="hidden">
          <RequestDraftDetail
            key={selectedDraftId}
            draftId={selectedDraftId}
            onResolved={handleResolved}
            onStatusChange={setStatus}
          />
        </Box>
      </Stack>
    </Stack>
  );
};
